/**
 * /student/exam/:attemptId/run — the exam itself.
 *
 * Layout follows the convention of a computer-based exam hall: one question at a
 * time, a fixed action row (Clear / Mark & Next / Save & Next), and the palette
 * pinned to the right.
 *
 * Everything that could lose work is defended:
 *   - answers autosave on change and survive reloads (AnswerQueue)
 *   - the countdown is the server's, not the laptop's (ServerClock)
 *   - webcam recording keeps chunking into IndexedDB regardless of network
 *   - proctoring LOGS; it never blocks input or ends the exam
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Calculator as CalculatorIcon,
  Eraser,
  Flag,
  MicOff,
  Monitor,
  Send,
  Upload,
  Video,
} from "lucide-react";
import { Button, Card, Dialog, useToast } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useAttempt } from "@/lib/exam/useAttempt";
import { isAnswered } from "@/lib/exam/answerState";
import { formatDuration } from "@/lib/exam/serverClock";
import { useProctor } from "@/lib/proctor";
import {
  combineUploadStatus,
  useCamera,
  useRecorder,
  useAdaptiveBitrate,
  useRecordingUi,
  useScreenShare,
  useSilentMicReport,
  shouldNudge,
  nudgeMessage,
  useUploadHealth,
  type CameraState,
  type ScreenShareState,
} from "@/lib/recorder";
import { useVersionWatch } from "@/lib/version";
import { setStage } from "@/lib/exam-api";
import { ExamTimer } from "./components/ExamTimer";
import { ProctorBanner } from "./components/ProctorBanner";
import { ReviewHint } from "./components/ReviewHint";
import { Scratchpad } from "./components/Scratchpad";
import { QuestionPalette } from "./components/QuestionPalette";
import { QuestionView } from "./components/QuestionView";
import { RecordingIndicator } from "./components/RecordingIndicator";

// Lazy so mathjs (which the calculator pulls in) is a separate chunk, fetched
// only when the student first opens the calculator — not part of the exam's
// initial load.
const Calculator = lazy(() => import("./components/Calculator"));

export function ExamRunnerPage() {
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const attempt = useAttempt({ attemptId });
  const [index, setIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  // Audio ON — see the note in SetupPage. Best-effort: a refused microphone
  // costs the sound, never the video.
  const camera = useCamera({ facingMode: "user", audio: true });

  const proctor = useProctor({
    attemptId,
    active: attempt.status === "in_progress",
    clock: attempt.clock,
    startedAtMs: attempt.startedAtMs,
    stream: camera.stream,
    requireFullscreen: true,
  });

  // The queue lengths the bitrate policy watches.
  //
  // Mirrored into state rather than read straight off `recorder.status`,
  // because the recorder is built BELOW using the bitrate this produces —
  // reading it directly would be a cycle. A single render of lag is irrelevant
  // against a policy window of a full minute.
  const [webcamPending, setWebcamPending] = useState(0);
  const [screenPending, setScreenPending] = useState(0);

  // Drops the encode rate when the upload cannot keep up. Fixed bitrates mean
  // that on a slow uplink the app produces bytes several times faster than it
  // can send them, so the backlog grows for the whole exam and never recovers
  // — and warning the student does not help, because there is nothing they can
  // do about their own connection mid-exam. Making less data is the only real
  // lever. Starts at the same 300 kbps and only ever descends; see
  // `adaptiveBitrate.ts` for why it will not climb back.
  const webcamBitrate = useAdaptiveBitrate({
    source: "webcam",
    pending: webcamPending,
    enabled: attempt.status === "in_progress",
  });

  const recorder = useRecorder({
    attemptId: attempt.status === "in_progress" ? attemptId : null,
    source: "webcam",
    stream: camera.stream,
    clock: attempt.clock,
    // 300 kbps at 640x480/15fps keeps the face recognizable for proctoring
    // while roughly halving the webcam's mobile-data cost — and falls further
    // if the connection cannot carry even that.
    videoBitsPerSecond: webcamBitrate,
    recording: attempt.status === "in_progress",
    // If the webcam track dies mid-exam, re-acquire it so the recording is one
    // continuous stream from start to submit. useCamera also re-acquires on its
    // own track-end; this is the direct line from the recorder.
    onStreamLost: () => void camera.request(),
    // A recorder that goes quiet without its track ending raises nothing on its
    // own — every other watchdog here watches the TRACK. Report it as a webcam
    // stop, because from the reviewer's point of view that is exactly what it
    // is: the footage ends here.
    onStall: ({ silentForMs, willRestart }) =>
      proctor.record("WEBCAM_STOPPED", {
        reason: "recorder produced no video",
        silent_for_ms: silentForMs,
        recovering: willRestart,
      }),
  });

  useEffect(() => {
    setWebcamPending(recorder.status.upload.pending);
  }, [recorder.status.upload.pending]);

  // Single honest recording state for the always-on corner indicator.
  const recUi = useRecordingUi({
    wanted: attempt.status === "in_progress",
    cameraState: camera.state,
    recording: recorder.status.recording,
    stalled: recorder.status.stalled,
  });

  /* -- screen capture (third source, desktop only) ------------------- */

  // Adopts the whole-screen share acquired during setup (parked in a module
  // singleton so it survives the setup → run navigation).
  const screen = useScreenShare();
  const inProgress = attempt.status === "in_progress";

  const screenBitrate = useAdaptiveBitrate({
    source: "screen",
    pending: screenPending,
    enabled: inProgress,
  });

  const screenRecorder = useRecorder({
    attemptId: inProgress ? attemptId : null,
    source: "screen",
    stream: screen.stream,
    clock: attempt.clock,
    // 500 kbps. Screen text stays crisp because the framerate is capped low
    // (~8fps, in useScreenShare) rather than the resolution — static exam
    // content barely changes, so low fps saves the most data. Drops further
    // under a sustained backlog.
    videoBitsPerSecond: screenBitrate,
    recording: inProgress,
    // No onStreamLost: a screen share cannot be silently re-acquired — it needs
    // a user gesture, so recovery goes through the blocking re-share prompt.
    onStall: ({ silentForMs, willRestart }) =>
      proctor.record("SCREEN_SHARE_STOPPED", {
        reason: "recorder produced no video",
        silent_for_ms: silentForMs,
        recovering: willRestart,
      }),
  });

  useEffect(() => {
    setScreenPending(screenRecorder.status.upload.pending);
  }, [screenRecorder.status.upload.pending]);

  const screenUi = useRecordingUi({
    wanted: inProgress,
    cameraState: screenStateToCamera(screen.state),
    recording: screenRecorder.status.recording,
    stalled: screenRecorder.status.stalled,
  });

  // Combined upload health across BOTH laptop sources — the loud signal that
  // footage is (or is NOT) reaching the server.
  const uploadHealth = useUploadHealth(
    combineUploadStatus([recorder.status.upload, screenRecorder.status.upload]),
    // A source that is producing nothing has an EMPTY queue, so the queue's own
    // numbers read as perfectly healthy. Recording health has to be told about
    // production separately or it reports "ok" on a dead recording.
    //
    // Derived from the UI states rather than the raw `stalled` flags, because
    // those are only one of the ways a recording ends up wanted-but-absent —
    // a recorder that simply is not running has `stalled: false` and was still
    // reporting "ok" while producing nothing.
    recUi.state === "stopped" || screenUi.state === "stopped",
  );

  /**
   * The footage is piling up and not landing — said out loud, not in the
   * corner.
   *
   * A student mid-question does not read a status pill, and this is the one
   * recording failure they can actually do something about: the uplink is
   * losing to the encoder, and moving the phone or the router fixes it. Shown
   * at ten queued segments, then not again before twenty, then thirty — see
   * `uploadBacklogNudge`.
   */
  const [backlogShownAt, setBacklogShownAt] = useState(0);
  const [backlogOpen, setBacklogOpen] = useState(false);
  useEffect(() => {
    const pending = screenRecorder.status.upload.pending;

    // Queue cleared: the incident is over, and the NEXT one has to start the
    // ladder again. Without this, one bad patch early on raises the bar for
    // the rest of the exam and the outage that actually loses footage passes
    // without a word.
    if (pending === 0 && backlogShownAt !== 0) setBacklogShownAt(0);

    if (!inProgress) {
      setBacklogOpen(false);
      return;
    }

    // "stalled" is the health state that means a backlog with no successful
    // upload for 45 seconds — footage genuinely not landing, rather than an
    // uplink that is merely slow.
    const stuck =
      uploadHealth.state === "stalled" ||
      uploadHealth.state === "not-recording";
    if (!shouldNudge(pending, backlogShownAt, stuck)) return;

    setBacklogShownAt(pending);
    setBacklogOpen(true);
    proctor.record("UPLOAD_BACKLOG", {
      pending_segments: pending,
      upload_state: uploadHealth.state,
    });
  }, [
    inProgress,
    screenRecorder.status.upload.pending,
    uploadHealth.state,
    backlogShownAt,
    proctor,
  ]);

  // Detect a stale cached bundle (a leading suspect for silent upload failures).
  const version = useVersionWatch();

  // Log screen-share proctor events on the transitions that matter.
  const prevScreenRef = useRef<ScreenShareState>(screen.state);
  useEffect(() => {
    const prev = prevScreenRef.current;
    prevScreenRef.current = screen.state;
    if (!inProgress || prev === screen.state) return;
    if (screen.state === "ended") {
      proctor.record("SCREEN_SHARE_STOPPED", { reason: "stopped sharing" });
    } else if (screen.state === "unfull") {
      proctor.record("SCREEN_SHARE_NOT_FULL", {
        reason: "shared a tab/window",
      });
    }
  }, [screen.state, inProgress, proctor]);

  // While recording, the student must keep a whole-screen share up. Anything
  // else is a blocking state until they re-share.
  //
  // `screenUi.state === "stopped"` is in here as well as the share states,
  // and it is the case that actually bites: the share itself can stay
  // perfectly "ready" while the RECORDER goes quiet. Gating only on the share
  // meant the corner indicator said "Screen stopped" in red and nothing
  // offered a way to fix it — the student could see the problem and not act
  // on it. A stalled screen recorder needs a fresh `getDisplayMedia`, which
  // needs a user gesture, which means it has to be a button.
  //
  // `screenUi.state` already carries a 15s grace, so a momentary gap does not
  // throw a modal over a live exam.
  const screenBlocked =
    inProgress &&
    (screen.state === "ended" ||
      screen.state === "unfull" ||
      screen.state === "denied" ||
      screen.state === "error" ||
      screenUi.state === "stopped");

  /* -- subjects & ordering (multi-subject paper) --------------------- */

  // Subjects in first-appearance order. The paper is tabbed and grouped by
  // these; questions are shown grouped by subject so Save & Next flows through
  // one subject before the next, and the student can jump between subjects
  // freely — the standard multi-subject sitting, under one global timer.
  const subjects = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of attempt.paper) {
      if (!seen.has(item.question.subject_id)) {
        seen.set(item.question.subject_id, item.question.subject_name);
      }
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [attempt.paper]);

  const orderedPaper = useMemo(
    () =>
      subjects.flatMap((s) =>
        attempt.paper.filter((it) => it.question.subject_id === s.id),
      ),
    [subjects, attempt.paper],
  );

  const current = orderedPaper[index];
  const currentId = current?.question.id ?? null;
  const activeSubjectId = current?.question.subject_id ?? null;

  /* -- visit tracking ------------------------------------------------ */

  // Depends on the stable callback, NOT on the whole `attempt` handle — that
  // object is rebuilt every render, which would re-run this on every keystroke.
  const visitQuestion = attempt.visitQuestion;
  useEffect(() => {
    if (currentId) visitQuestion(currentId);
  }, [currentId, visitQuestion]);

  /* -- the recording has no sound, and the reviewer is told so ------- */

  /**
   * `MIC_OFF`, logged once per silent stretch.
   *
   * Without this the reviewer opens an attempt, hears nothing, and has no way
   * to tell a quiet room from a microphone that was never on — and the second
   * one is the case where "nobody spoke to them" is not evidence of anything.
   * Logged rather than blocked: a laptop with no working microphone is common
   * and cannot be fixed mid-paper.
   *
   * `logged` resets when audio comes BACK, so a mic that drops out twice is
   * reported twice, while a mic that is simply absent is reported once.
   */
  /**
   * A microphone that is ON and hearing NOTHING — the failure `MIC_OFF` cannot
   * catch, because that one fires on a MISSING track and this track is live.
   * See `useSilentMicReport`.
   */
  useSilentMicReport({
    stream: camera.stream,
    active: attempt.status === "in_progress" && camera.hasAudio,
    onSilent: (deadForMs) =>
      proctor.record("MIC_SILENT", { dead_for_ms: deadForMs }),
  });

  const micOffLogged = useRef(false);
  useEffect(() => {
    if (attempt.status !== "in_progress") return;

    if (camera.hasAudio) {
      micOffLogged.current = false;
      return;
    }
    // Not until the camera has settled — "no audio" is meaningless while
    // getUserMedia is still in flight, and firing then would report a mic that
    // is about to work.
    if (camera.state !== "ready") return;
    if (micOffLogged.current) return;

    micOffLogged.current = true;
    proctor.record("MIC_OFF", { mic_state: camera.micState });
  }, [attempt.status, camera.hasAudio, camera.state, camera.micState, proctor]);

  /* -- keep the phone on the "recording" stage ----------------------- */

  useEffect(() => {
    if (attempt.status !== "in_progress") return;
    if (attempt.stage === "recording") return;
    void setStage(attemptId, "recording").catch(() => {
      /* the phone keeps recording regardless; this only syncs its label */
    });
  }, [attemptId, attempt.status, attempt.stage]);

  /* -- leaving the page --------------------------------------------- */

  useEffect(() => {
    if (attempt.status !== "in_progress") return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [attempt.status]);

  /* -- server-side auto-submit -------------------------------------- */

  // The SERVER closes the attempt when the timer runs out (docs/API.md §3.6);
  // the client never does it. All this does is follow the state it is told.
  const submittedRef = useRef(false);
  useEffect(() => {
    if (submittedRef.current) return;
    if (attempt.status === "submitted" || attempt.status === "auto_submitted") {
      submittedRef.current = true;
      void recorder.finishUploads();
      void screenRecorder.finishUploads();
      screen.stop();
      void proctor.finish();
      navigate(`/student/exam/${attemptId}/submitted`, { replace: true });
    }
  }, [
    attempt.status,
    attemptId,
    navigate,
    recorder,
    screenRecorder,
    screen,
    proctor,
  ]);

  /* -- actions -------------------------------------------------------- */

  const goTo = useCallback(
    (nextIndex: number) => {
      setIndex(Math.max(0, Math.min(nextIndex, orderedPaper.length - 1)));
    },
    [orderedPaper.length],
  );

  const goToQuestion = useCallback(
    (questionId: string) => {
      const i = orderedPaper.findIndex((it) => it.question.id === questionId);
      if (i >= 0) setIndex(i);
    },
    [orderedPaper],
  );

  const selectSubject = useCallback(
    (subjectId: string) => {
      const i = orderedPaper.findIndex(
        (it) => it.question.subject_id === subjectId,
      );
      if (i >= 0) setIndex(i);
    },
    [orderedPaper],
  );

  const clearResponse = useCallback(() => {
    if (!currentId) return;
    attempt.setResponse(currentId, null);
  }, [attempt, currentId]);

  const saveAndNext = useCallback(() => {
    if (!currentId) return;
    // The answer is already saved — it went out the moment it changed. This
    // button exists because the student expects it to, and it advances.
    attempt.setMarkedForReview(currentId, false);
    goTo(index + 1);
  }, [attempt, currentId, goTo, index]);

  /**
   * How long the student has been on this question, this visit.
   *
   * Local and deliberately simple: it only feeds the review hint, which has a
   * 45-second threshold, so a second of drift either way is irrelevant. The
   * authoritative per-question timing is the server's.
   */
  const [msOnQuestion, setMsOnQuestion] = useState(0);
  useEffect(() => {
    setMsOnQuestion(0);
    if (!currentId) return;
    const startedAt = Date.now();
    const id = setInterval(
      () => setMsOnQuestion(Date.now() - startedAt),
      1_000,
    );
    return () => clearInterval(id);
  }, [currentId]);

  /** Mark without advancing — the hint offers it where they already are. */
  const markHere = useCallback(() => {
    if (currentId) attempt.setMarkedForReview(currentId, true);
  }, [attempt, currentId]);

  const markAndNext = useCallback(() => {
    if (!currentId) return;
    attempt.setMarkedForReview(currentId, true);
    goTo(index + 1);
  }, [attempt, currentId, goTo, index]);

  const doSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      submittedRef.current = true;
      await attempt.submit();
      await Promise.all([
        recorder.finishUploads(),
        screenRecorder.finishUploads(),
      ]);
      screen.stop();
      await proctor.finish();
      navigate(`/student/exam/${attemptId}/submitted`, { replace: true });
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      setConfirmOpen(false);
      toast({
        tone: "error",
        title: "Could not submit",
        description:
          err instanceof Error ? err.message : "Please try again in a moment.",
      });
    }
  }, [
    attempt,
    recorder,
    screenRecorder,
    screen,
    proctor,
    navigate,
    attemptId,
    toast,
  ]);

  /* -- palette ------------------------------------------------------- */

  // One palette group per subject, numbered globally (1..N in the ordered paper)
  // so every question has a unique number.
  const paletteGroups = useMemo(
    () =>
      subjects.map((s) => ({
        subjectName: s.name,
        items: orderedPaper
          .map((it, i) => ({ it, i }))
          .filter((x) => x.it.question.subject_id === s.id)
          .map((x) => ({ questionId: x.it.question.id, number: x.i + 1 })),
      })),
    [subjects, orderedPaper],
  );

  /* -- submit lock --------------------------------------------------- */

  // Recomputed every render (the display tick re-renders us), so the countdown
  // ticks. Enforcement is server-side; this is the client-side gate + display.
  const submitLockRemainingMs =
    attempt.submitUnlocksAtMs === null
      ? 0
      : Math.max(0, attempt.submitUnlocksAtMs - attempt.clock.now());
  const submitLocked = submitLockRemainingMs > 0;

  /* -- render --------------------------------------------------------- */

  if (attempt.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app">
        <p className="text-xs text-ink-faint">Loading your exam…</p>
      </div>
    );
  }

  if (attempt.error && attempt.paper.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-8">
        <Card>
          <div className="p-4">
            <h1 className="text-sm font-semibold text-ink">
              Could not open this exam
            </h1>
            <p className="mt-1 text-xs text-ink-muted">{attempt.error}</p>
            <Button
              className="mt-4"
              variant="secondary"
              onClick={() => navigate("/student")}
            >
              Back to my exams
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-app">
      <header className="sticky top-0 z-10 border-b border-border bg-surface">
        <div className="flex h-11 items-center gap-3 px-4">
          <span className="truncate text-xs font-semibold text-ink">
            {attempt.exam?.title ?? "Exam"}
          </span>
          <span className="text-border-strong">/</span>
          <span className="hidden truncate text-xs text-ink-muted sm:inline">
            {attempt.exam?.subject_name}
          </span>

          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              aria-label="Calculator"
              aria-pressed={calcOpen}
              title="Calculator"
              onClick={() => setCalcOpen((o) => !o)}
              className={cn(
                "flex h-7 items-center gap-1.5 rounded-md border px-2 text-2xs font-medium transition-colors",
                calcOpen
                  ? "border-accent bg-accent-soft text-accent"
                  : "border-border-strong bg-surface text-ink-muted hover:bg-surface-sunken",
              )}
            >
              <CalculatorIcon className="size-3.5" />
              <span className="hidden sm:inline">Calculator</span>
            </button>
            <RecordingDot
              recording={recorder.status.recording}
              hasAudio={camera.hasAudio}
              pending={recorder.status.upload.pending}
            />
            <SaveIndicator
              pending={attempt.save.pending}
              saving={attempt.save.saving}
              degraded={attempt.save.degraded}
            />
            <ExamTimer
              remainingMs={attempt.remainingMs}
              critical={attempt.timeCritical}
            />
          </div>
        </div>

        <ProctorBanner
          fullscreenActive={proctor.fullscreenActive}
          online={proctor.online}
          // An exam that never asked for a phone must not warn about one.
          // Reporting a camera the student was never told to set up is a
          // warning they cannot act on, in the middle of an exam — and it
          // teaches them to ignore the banner that matters.
          phoneAlive={attempt.exam?.require_phone ? attempt.phoneAlive : true}
          violations={proctor.state.totalViolations}
          saveDegraded={attempt.save.degraded}
          pendingUploads={recorder.status.upload.pending}
          onReturnToFullscreen={() => void proctor.requestFullscreen()}
        />
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 gap-4 px-4 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {subjects.length > 1 ? (
            <SubjectTabs
              subjects={subjects}
              activeSubjectId={activeSubjectId}
              onSelect={selectSubject}
            />
          ) : null}

          <Card className="flex-1">
            {current ? (
              <QuestionView
                item={current}
                number={index + 1}
                total={orderedPaper.length}
                response={attempt.responses.get(current.question.id) ?? null}
                onChange={(response) =>
                  attempt.setResponse(current.question.id, response)
                }
              />
            ) : (
              <p className="p-4 text-xs text-ink-faint">
                This exam has no questions.
              </p>
            )}
          </Card>

          <ReviewHint
            questionId={currentId}
            answered={isAnswered(attempt.responses.get(currentId ?? ""))}
            markedForReview={
              currentId ? attempt.markedForReview.has(currentId) : false
            }
            msOnQuestion={msOnQuestion}
            onMark={markHere}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              icon={<Eraser />}
              onClick={clearResponse}
              disabled={!isAnswered(attempt.responses.get(currentId ?? ""))}
            >
              Clear response
            </Button>
            <Button variant="secondary" icon={<Flag />} onClick={markAndNext}>
              Mark for review and next
            </Button>

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => goTo(index - 1)}
                disabled={index === 0}
              >
                Previous
              </Button>
              <Button variant="primary" onClick={saveAndNext}>
                {index === orderedPaper.length - 1 ? "Save" : "Save and next"}
              </Button>
            </div>
          </div>
        </div>

        <aside className="hidden w-64 shrink-0 lg:block">
          <Card className="sticky top-20 p-4">
            <QuestionPalette
              groups={paletteGroups}
              responses={attempt.responses}
              markedForReview={attempt.markedForReview}
              visited={attempt.visited}
              activeQuestionId={currentId}
              onSelect={goToQuestion}
            />

            <Button
              variant="primary"
              icon={<Send />}
              className="mt-4 w-full"
              disabled={submitLocked}
              onClick={() => setConfirmOpen(true)}
            >
              Submit exam
            </Button>
            {submitLocked ? (
              <p className="tabular mt-2 text-center text-2xs text-ink-muted">
                Submit unlocks in{" "}
                <span className="font-medium text-ink">
                  {formatDuration(submitLockRemainingMs)}
                </span>
              </p>
            ) : null}
          </Card>
        </aside>
      </main>

      {/* Rough work. Nothing here is uploaded — the screen recording already
          captures the working, in context and in order, so a second copy would
          be paid for on the connection this app tries hardest to protect. */}
      {attempt.exam?.allow_scratchpad ? (
        <Scratchpad attemptId={attemptId} />
      ) : null}

      {/* Always-on recording indicator — the student must see BOTH the webcam
          and the screen capture are alive for the whole exam. */}
      <RecordingIndicator
        stream={camera.stream}
        state={recUi.state}
        elapsedMs={recUi.elapsedMs}
        screenState={screenUi.state}
        upload={uploadHealth}
        onRestartCamera={() => void camera.request()}
        onReshareScreen={() => void screen.request()}
      />

      {/* Build stamp — lets us tell if a student is on a stale cached bundle,
          and prompts a reload when a newer one is being served. */}
      {version.updateAvailable ? (
        <div className="fixed bottom-3 left-3 z-30 max-w-xs rounded-md border border-accent/40 bg-accent-soft px-3 py-2">
          <p className="text-2xs font-medium text-accent">
            A newer version is available.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-1 text-2xs font-semibold text-accent underline"
          >
            Reload to update
          </button>
        </div>
      ) : (
        <span className="tabular fixed bottom-1 left-2 z-20 text-2xs text-ink-faint/60">
          build {version.buildId}
        </span>
      )}

      {/* Screen sharing dropped mid-exam — block until it is re-shared. */}
      {screenBlocked ? (
        <ScreenShareBlocker
          state={screen.state}
          // Distinguishes "you stopped sharing" from "the recording died while
          // you were still sharing" — different causes, and the student should
          // be told which one they are looking at.
          recorderStalled={
            screenUi.state === "stopped" && screen.state === "ready"
          }
          onReshare={() => void screen.request()}
        />
      ) : null}

      {/* Desktop-only: the exam is taken on the laptop; on a phone-width screen
          the panel would cover the paper, so it is hidden below `lg`. */}
      {calcOpen ? (
        <div className="hidden lg:block">
          <Suspense fallback={null}>
            <Calculator onClose={() => setCalcOpen(false)} />
          </Suspense>
        </div>
      ) : null}

      {/*
        Deliberately NOT a <dialog>: a modal traps focus, and this one opens on
        a timer the student did not touch. Mid-answer it would swallow the next
        keystrokes and the next Space would dismiss it unread — losing typed
        characters during a timed paper to tell someone their uplink is slow.
        It sits above the paper, says its piece, and leaves the keyboard alone.
      */}
      {backlogOpen ? (
        <div
          role="status"
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-lg rounded-md border border-review/40 bg-review-soft p-3 shadow-lg sm:inset-x-auto sm:right-4"
        >
          <p className="text-xs font-medium text-review">
            Your internet is not keeping up with the recording
          </p>
          <p className="mt-1 text-2xs text-ink-muted">
            {nudgeMessage(attempt.exam?.require_phone === true)}
          </p>
          <p className="mt-1 text-2xs text-ink-faint">
            Your answers are saved separately, so the exam itself is not
            affected. Keep this page open.
          </p>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={() => setBacklogOpen(false)}>
              Got it
            </Button>
          </div>
        </div>
      ) : null}

      <SubmitDialog
        open={confirmOpen}
        submitting={submitting}
        answered={attempt.counts.answered}
        unanswered={attempt.counts.unanswered}
        markedForReview={attempt.counts.markedForReview}
        pendingSaves={attempt.save.pending}
        pendingUploads={recorder.status.upload.pending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void doSubmit()}
      />
    </div>
  );
}

/** Maps the screen-share state onto the camera-state vocabulary the shared
 *  recording state machine understands. */
function screenStateToCamera(state: ScreenShareState): CameraState {
  switch (state) {
    case "ready":
      return "ready";
    case "requesting":
      return "requesting";
    case "idle":
      return "idle";
    default:
      // ended / unfull / denied / error → a stopped recording that needs action.
      return "error";
  }
}

/**
 * Full-screen blocking prompt shown when the whole-screen share drops mid-exam.
 * The student cannot continue until they re-share; re-sharing is a real gesture
 * (this button), which `getDisplayMedia` requires.
 */
function ScreenShareBlocker({
  state,
  recorderStalled,
  onReshare,
}: {
  state: ScreenShareState;
  recorderStalled: boolean;
  onReshare: () => void;
}) {
  const message = recorderStalled
    ? "Your screen is still shared, but it stopped being recorded. Share your entire screen again to restart the recording."
    : state === "unfull"
      ? "You shared a tab or window, not your whole screen. You must share your ENTIRE screen to continue."
      : state === "denied"
        ? "Screen sharing is required. Share your entire screen to continue your exam."
        : state === "error"
          ? "Screen sharing could not start. Share your entire screen to continue."
          : "Screen sharing stopped. You must re-share your entire screen to continue.";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4">
      <div className="w-full max-w-md rounded-md border border-violation/40 bg-surface p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-violation">
          <AlertTriangle className="size-4" />
          {recorderStalled
            ? "Screen recording stopped"
            : "Screen sharing stopped"}
        </h2>
        <p className="mt-2 text-xs text-ink-muted">{message}</p>
        <p className="mt-2 text-2xs text-ink-faint">
          Your exam and its timer are still running. Pick{" "}
          <span className="font-medium text-ink">Entire Screen</span> in the
          picker.
        </p>
        <Button
          variant="primary"
          icon={<Monitor />}
          className="mt-4 w-full"
          onClick={onReshare}
        >
          Share my entire screen
        </Button>
      </div>
    </div>
  );
}

/** Subject tab bar for a multi-subject paper. */
function SubjectTabs({
  subjects,
  activeSubjectId,
  onSelect,
}: {
  subjects: { id: string; name: string }[];
  activeSubjectId: string | null;
  onSelect: (subjectId: string) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Subjects"
      className="flex flex-wrap gap-1 overflow-x-auto rounded-md border border-border bg-surface p-1"
    >
      {subjects.map((s) => {
        const active = s.id === activeSubjectId;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(s.id)}
            className={cn(
              "shrink-0 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors",
              active
                ? "bg-accent-soft text-accent"
                : "text-ink-muted hover:bg-surface-sunken",
            )}
          >
            {s.name}
          </button>
        );
      })}
    </div>
  );
}

function RecordingDot({
  recording,
  hasAudio,
  pending,
}: {
  recording: boolean;
  /** Shown alongside, because a silent recording looks identical to a
   *  working one until somebody tries to play it back. */
  hasAudio: boolean;
  pending: number;
}) {
  return (
    <span
      className="flex items-center gap-1.5"
      title={
        recording
          ? hasAudio
            ? "Your webcam and microphone are recording"
            : "Your webcam is recording — there is no microphone, so this recording has no sound"
          : "The webcam is not recording"
      }
    >
      <Video
        className={cn(
          "size-3.5",
          recording ? "text-violation" : "text-ink-faint",
        )}
      />
      {recording ? (
        <MicOff
          className={cn("size-3.5 text-warn", hasAudio && "hidden")}
          aria-label="No microphone"
        />
      ) : null}
      <span className="hidden text-2xs text-ink-muted sm:inline">
        {recording ? "Recording" : "Camera off"}
        {pending > 0 ? (
          <span className="tabular text-ink-faint"> · {pending} queued</span>
        ) : null}
      </span>
    </span>
  );
}

function SaveIndicator({
  pending,
  saving,
  degraded,
}: {
  pending: number;
  saving: boolean;
  degraded: boolean;
}) {
  const label = degraded
    ? "Retrying…"
    : saving || pending > 0
      ? "Saving…"
      : "Saved";

  return (
    <span
      className={cn(
        "hidden text-2xs sm:inline",
        degraded ? "text-review" : "text-ink-faint",
      )}
    >
      {label}
    </span>
  );
}

function SubmitDialog({
  open,
  submitting,
  answered,
  unanswered,
  markedForReview,
  pendingSaves,
  pendingUploads,
  onClose,
  onConfirm,
}: {
  open: boolean;
  submitting: boolean;
  answered: number;
  unanswered: number;
  markedForReview: number;
  pendingSaves: number;
  pendingUploads: number;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const rows = [
    { label: "Answered", value: answered, tone: "text-answered" },
    { label: "Unanswered", value: unanswered, tone: "text-violation" },
    { label: "Marked for review", value: markedForReview, tone: "text-review" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Submit your exam?"
      description="You cannot change your answers after submitting."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Keep working
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit exam"}
          </Button>
        </>
      }
    >
      <dl className="flex flex-col divide-y divide-border border-y border-border">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between py-2"
          >
            <dt className="text-xs text-ink-muted">{row.label}</dt>
            <dd className={cn("tabular text-sm font-medium", row.tone)}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      {unanswered > 0 ? (
        <p className="mt-3 text-xs text-ink-muted">
          <span className="tabular font-medium text-violation">
            {unanswered}
          </span>{" "}
          {unanswered === 1 ? "question is" : "questions are"} still unanswered.
          Unanswered questions score zero and are not penalised.
        </p>
      ) : null}

      {submitting ? (
        // While finishing, say what is happening — a silent 10-15s wait reads as
        // a hang. This ticks down live as segments upload.
        <p className="mt-3 flex items-center gap-1.5 border-t border-border pt-3 text-2xs text-ink-muted">
          <Upload className="size-3 text-ink-faint" />
          {pendingUploads > 0
            ? `Uploading your recording — ${pendingUploads} video ${pendingUploads === 1 ? "segment" : "segments"} left…`
            : "Finishing up…"}
        </p>
      ) : pendingSaves > 0 || pendingUploads > 0 ? (
        <p className="mt-3 border-t border-border pt-3 text-2xs text-ink-faint">
          Submitting will finish saving your work first
          {pendingUploads > 0
            ? ` and upload ${pendingUploads} remaining video ${pendingUploads === 1 ? "segment" : "segments"}`
            : ""}
          .
        </p>
      ) : null}
    </Dialog>
  );
}
